import React, { useEffect, useRef } from 'react';
import { usePlayer } from '../contexts/PlayerContext';
import { useTheme } from '../contexts/ThemeContext';
import { hexToRgb } from '../utils/color';
interface WaveformProps {
  className?: string;
  /** Bars are mirrored around the centre line when true. */
  mirrored?: boolean;
  bars?: number;
}

/**
 * Live frequency wave drawn from the AnalyserNode. Flat and still when paused,
 * so it reads as a genuine signal rather than an idle decoration.
 */
export function Waveform({
  className = '',
  mirrored = true,
  bars = 48
}: WaveformProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const {
    getSpectrum,
    getLevel,
    isPlaying
  } = usePlayer();
  const {
    accent,
    secondary
  } = useTheme();
  const spectrumFn = useRef(getSpectrum);
  const levelFn = useRef(getLevel);
  const playing = useRef(isPlaying);
  spectrumFn.current = getSpectrum;
  levelFn.current = getLevel;
  playing.current = isPlaying;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const accentRgb = hexToRgb(accent);
    const secondaryRgb = hexToRgb(secondary);
    const dpr = Math.min(window.devicePixelRatio, 2);
    const heights = new Float32Array(bars);
    let width = 0;
    let height = 0;
    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    let frame = 0;
    const tick = () => {
      frame = requestAnimationFrame(tick);
      const spectrum = spectrumFn.current();
      const level = playing.current ? levelFn.current() : 0;
      const t = performance.now() / 1000;
      for (let i = 0; i < bars; i += 1) {
        let target: number;
        if (spectrum.length > 4) {
          const from = Math.floor(i / bars * spectrum.length * 0.8);
          target = spectrum[Math.min(from, spectrum.length - 1)];
        } else {
          // No analyser data available: derive a soft wave from the level.
          target = playing.current ? level * (0.55 + 0.45 * Math.sin(t * 2.4 + i * 0.35)) : 0;
        }
        if (!playing.current) target = 0;
        heights[i] += (target - heights[i]) * 0.22;
      }
      ctx.clearRect(0, 0, width, height);
      const gap = 2;
      const barWidth = Math.max(1.5, width / bars - gap);
      const mid = mirrored ? height / 2 : height;
      for (let i = 0; i < bars; i += 1) {
        const value = Math.min(1, heights[i]);
        const barHeight = Math.max(1.5, value * (mirrored ? height * 0.46 : height));
        const x = i * (barWidth + gap);
        const blend = i / bars;
        const r = Math.round(accentRgb[0] + (secondaryRgb[0] - accentRgb[0]) * blend);
        const g = Math.round(accentRgb[1] + (secondaryRgb[1] - accentRgb[1]) * blend);
        const b = Math.round(accentRgb[2] + (secondaryRgb[2] - accentRgb[2]) * blend);
        ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${0.35 + value * 0.55})`;
        if (mirrored) ctx.fillRect(x, mid - barHeight, barWidth, barHeight * 2);else ctx.fillRect(x, mid - barHeight, barWidth, barHeight);
      }
    };
    tick();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [accent, bars, mirrored, secondary]);
  return <canvas ref={canvasRef} aria-hidden="true" className={`block h-full w-full ${className}`} data-id="element-261" data-mp="2e8gnwr0mysbu" />;
}